"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { useAlert } from "@/contexts/AlertContext";
import { useUser } from "@/contexts/UserContext";
import { apiRemote } from "@/api";
import { StyledLink } from "@/components/StyledLink";

const STATUS_DRAFT = process.env.STATUS_DRAFT;
const STATUS_SUBMITTED = process.env.STATUS_SUBMITTED;
const STATUS_NOT_STARTED = process.env.STATUS_NOT_STARTED;
const STATUS_IN_PROGRESS = process.env.STATUS_IN_PROGRESS;
const STATUS_ENDED = process.env.STATUS_ENDED;

export default function Deliverable4() {
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRoundParts, loading, loadUserRound1 } = useThoughtCoCreate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const alert = useAlert();
  const { user } = useUser();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const localStorageKey = `draft_deliverable4_${user?.user_login}_${id}`;
  const [isCleared, setIsCleared] = useState(false);

  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(localStorageKey);
    return savedData ? JSON.parse(savedData) : {
      finalProposal: "",
      implementationSteps: "",
      resourcesNeeded: "",
      expectedOutcomes: "",
    };
  });

  useEffect(() => {
    if (!isCleared) {
      localStorage.setItem(localStorageKey, JSON.stringify(formData));
    }
  }, [formData]);

  const validateForm = () => {
    let newErrors: { [key: string]: string } = {};

    if (!formData.finalProposal) newErrors.finalProposal = "Required";
    if (!formData.implementationSteps)
      newErrors.implementationSteps = "Required";
    if (!formData.resourcesNeeded) newErrors.resourcesNeeded = "Required";
    if (!formData.expectedOutcomes) newErrors.expectedOutcomes = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const current_round = Number(id) - 1;
    const previous_round = Number(id) - 2;
    if (loading || !userRoundParts?.[current_round]) return;
    const roundData = userRoundParts[current_round];
    const previousRoundData = userRoundParts[previous_round];
    if (roundData.status == Number(STATUS_SUBMITTED)) {
      setIsSubmitted(true);
    } else {
      setIsSubmitted(false);
    }
    if (roundData.submission_data) {
      const submissionData = JSON.parse(roundData.submission_data);
      setFormData(submissionData);
    }
    // console.log(userRound, id, userRound[Number(id)].due_date);
    const dueDate = new Date(roundData.due_date);
    const today = new Date();
    // console.log(today, dueDate)
    const dueDateOnly = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    if (
      previousRoundData &&
      previousRoundData.status !== Number(STATUS_DRAFT) &&
      previousRoundData.status !== Number(STATUS_SUBMITTED)
    ) {
      setStatusMessage(
        `You need to complete the previous part to work on this one.`
      );
    } else {
      // if (todayOnly < dueDateOnly && (roundData.status !== Number(STATUS_SUBMITTED))) {
      //   setStatusMessage(
      //     `Submission is not open yet. Due date: ${roundData.due_date}`
      //   );
      // } else if (todayOnly > dueDateOnly && (roundData.status !== Number(STATUS_SUBMITTED))) {
      //   setStatusMessage(
      //     `Submission is overdue. Final deadline: ${roundData.due_date}`
      //   );
      // } else {
      //   setStatusMessage(null); // Within the allowed time, no message needed
      // }
      if (
        todayOnly > dueDateOnly &&
        roundData.status !== Number(STATUS_SUBMITTED)
      ) {
        setStatusMessage(
          `Submission is overdue. Final deadline: ${roundData.due_date}`
        );
        setIsDeadlinePassed(true)
      } else if (
        todayOnly > dueDateOnly
      ) {
        setIsDeadlinePassed(true)
      } else {
        setStatusMessage(null); // Within the allowed time, no message needed
        setIsDeadlinePassed(false)
      }
    }
  }, [id, userRoundParts, loading]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    setErrors({
      ...errors,
      [event.target.name]: "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        apiRemote
          .postStep({
            user: user?.user_login || "",
            round_part_id: Number(id),
            status: 2,
            submission_data: JSON.stringify(formData),
            step: 1,
          })
          .then((data) => {
            console.log(data);
            if (data.code == 200) {
              alert.success(data.message);
              setIsSubmitted(true);
              loadUserRound1();
              setIsCleared(true);
              localStorage.removeItem(localStorageKey);
            } else {
              alert.error(data.message);
            }
          })
          .finally(() => setIsSubmitting(false));
      } catch (error) {
        alert.error("Something was wrong!");
        console.log(error)
      }
      console.log("Form submitted successfully", formData);
    } else {
      console.log("Form has errors", errors);
    }
    // Handle submission logic here
  };

  const handleSaveDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    setIsSubmitting(true);
    if (validateForm()) {
      try {
        apiRemote
          .postStep({
            user: user?.user_login || "",
            round_part_id: Number(id),
            status: 1,
            submission_data: JSON.stringify(formData),
            step: 1,
          })
          .then((data) => {
            console.log(data);
            if (data.code == 200) {
              alert.success(data.message);
              setIsSubmitted(true);
              loadUserRound1();
              // setIsCleared(true);
              localStorage.removeItem(localStorageKey);
            } else {
              alert.error(data.message);
            }
          })
          .finally(() => setIsSubmitting(false));
      } catch (error) {
        alert.error("Something was wrong!");
        console.log(error)
      }
      console.log("Form submitted successfully", formData);
    } else {
      console.log("Form has errors", errors);
    }
  };

  const handleNext = () => {
    const nextId = Number(id) + 1;
    navigate(`../${nextId}`);
  };

  const handlePrevious = () => {
    const previousId = Number(id) - 1;
    navigate(`../${previousId}`);
  };

  return (
    <>
      {statusMessage ? (
        <Typography
          variant="body1"
          color={statusMessage.includes("overdue") ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {statusMessage}
        </Typography>
      ) : (
        <>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              "& .MuiTextField-root": {
                mb: 3,
              },
            }}
          >
            <Stack spacing={3}>
              <TextField
                label="Final Proposal"
                name="finalProposal"
                value={formData.finalProposal}
                onChange={handleChange}
                multiline
                rows={8}
                fullWidth
                placeholder="Enter your final refined proposal here..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "divider",
                    },
                  },
                }}
                error={!!errors.finalProposal} // Hiển thị viền đỏ nếu có lỗi
                helperText={errors.finalProposal}
                InputProps={{ readOnly: isDeadlinePassed }}
              />

              <TextField
                label="Implementation Steps"
                name="implementationSteps"
                value={formData.implementationSteps}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
                placeholder="Outline the steps to implement your proposal..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "divider",
                    },
                  },
                }}
                error={!!errors.implementationSteps} // Hiển thị viền đỏ nếu có lỗi
                helperText={errors.implementationSteps}
                InputProps={{ readOnly: isDeadlinePassed }}
              />

              <TextField
                label="Resources Needed"
                name="resourcesNeeded"
                value={formData.resourcesNeeded}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
                placeholder="List the resources required for implementation..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "divider",
                    },
                  },
                }}
                error={!!errors.resourcesNeeded} // Hiển thị viền đỏ nếu có lỗi
                helperText={errors.resourcesNeeded}
                InputProps={{ readOnly: isDeadlinePassed }}
              />

              <TextField
                label="Expected Outcomes"
                name="expectedOutcomes"
                value={formData.expectedOutcomes}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
                placeholder="Describe the expected outcomes of your proposal..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "divider",
                    },
                  },
                }}
                error={!!errors.expectedOutcomes} // Hiển thị viền đỏ nếu có lỗi
                helperText={errors.expectedOutcomes}
                InputProps={{ readOnly: isDeadlinePassed }}
              />
            </Stack>
          </Paper>

          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "space-between",
              mt: 3,
            }}
          >
            <Button
              variant="text"
              color="inherit"
              sx={{ color: "text.primary" }}
            >
              <StyledLink to={`../../dashboard`} style={{ color: "#000" }}>
                Back to Dashboard
              </StyledLink>
            </Button>
            <Box>
              {isSubmitting && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: "middle" }} />}
              {/* <Button
                variant="text"
                sx={{
                  mr: 2,
                  color: "text.primary",
                }}
                onClick={handleSaveDraft}
                disabled={isSubmitted || isSubmitting}
              >
                Save as Draft
              </Button> */}
              <Button
                variant="contained"
                sx={{
                  bgcolor: "black",
                  "&:hover": {
                    bgcolor: "grey.900",
                  },
                }}
                onClick={handleSubmit}
                disabled={isDeadlinePassed || isSubmitting}
              >
                Submit D4
              </Button>
              {isSubmitted && (
                <Button
                  variant="contained"
                  sx={{
                    ml: 2,
                    bgcolor: "blue",
                    "&:hover": {
                      bgcolor: "darkblue",
                    },
                  }}
                  onClick={handlePrevious}
                >
                  Previous
                </Button>
              )}
            </Box>
          </Stack>
        </>
      )}
    </>
  );
}
